import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { NewsCategoryOption, NewsItem, fetchPublicNews } from '@/services/news';

const allCategoryLabel = '全部消息';

export default function NewsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || '';
  const [categories, setCategories] = useState<NewsCategoryOption[]>([]);
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPublicNews({ NewsCategoryId: categoryParam || undefined, PerPage: 30 })
      .then((data) => {
        if (!alive) return;
        setCategories(data.categories);
        setItems(data.items);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [categoryParam]);

  const activeCategoryName = categories.find((category) => String(category.id) === categoryParam)?.name || allCategoryLabel;

  function selectCategory(categoryId: string) {
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden text-white pt-8 md:pt-14 pb-8 md:pb-12">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#0F2A1C]" />
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4A574]/8 blur-[120px]" />
          <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#2E8B57]/12 blur-[100px]" />

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-px h-5 bg-[#D4A574]" />
              <span className="text-xs font-semibold tracking-[0.2em] text-[#D4A574] uppercase">Latest News</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
              門市/活動/產品
              <span className="block text-[#D4A574] mt-2 md:mt-3">最新消息</span>
            </h1>
            <p className="mt-3 md:mt-4 text-white/80 text-sm md:text-base max-w-xl leading-relaxed">
              富聆聽門市公告、優惠活動與最新動態，掌握第一手資訊。
            </p>
          </div>

          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none">
            <path d="M0 100L60 90C120 80 240 60 360 50C480 40 600 38 720 44C840 50 960 62 1080 68C1200 74 1320 74 1380 74L1440 74V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="white" />
          </svg>
        </section>

        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Sidebar */}
            <aside className="md:w-56 shrink-0">
              <nav className="rounded-2xl border border-[#E8E0D6] bg-white overflow-hidden md:sticky md:top-24">
                <button
                  type="button"
                  onClick={() => selectCategory('')}
                  className={`w-full flex items-center gap-2 px-4 py-3.5 text-left text-sm font-medium border-b border-[#E8E0D6] transition-colors ${
                    !categoryParam ? 'bg-[#1B3D2A]/8 text-[#1B3D2A] font-bold' : 'text-[#5A6B5A] hover:bg-[#FAF8F5]'
                  }`}
                >
                  {!categoryParam && <i className="ri-arrow-right-s-fill text-[#D4A574]" />}
                  {allCategoryLabel}
                </button>
                {categories.map((category) => {
                  const active = String(category.id) === categoryParam;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => selectCategory(String(category.id))}
                      className={`w-full flex items-center gap-2 px-4 py-3.5 text-left text-sm font-medium border-b border-[#E8E0D6] last:border-b-0 transition-colors ${
                        active ? 'bg-[#1B3D2A]/8 text-[#1B3D2A] font-bold' : 'text-[#5A6B5A] hover:bg-[#FAF8F5]'
                      }`}
                    >
                      {active && <i className="ri-arrow-right-s-fill text-[#D4A574]" />}
                      {category.name}
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* List */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-[#E8E0D6] p-5 md:p-7">
                <div className="flex items-center justify-between border-b border-[#E8E0D6] pb-4 mb-2">
                  <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">{activeCategoryName}</h2>
                  <span className="hidden sm:block text-xs font-semibold tracking-wider text-[#9A8A7A] uppercase">主題 / 時間</span>
                </div>

                {loading ? (
                  <div className="py-14 text-center text-[#9A8A7A]">載入中...</div>
                ) : items.length === 0 ? (
                  <div className="py-14 text-center text-[#9A8A7A]">目前尚無最新消息。</div>
                ) : (
                  <ul>
                    {items.map((item) => (
                      <li key={item.id} className="border-b border-[#E8E0D6] last:border-b-0">
                        <button
                          type="button"
                          onClick={() => navigate(`/news/${item.slug || item.id}`)}
                          className="w-full flex items-center justify-between gap-4 py-4 text-left group"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#D4A574] shrink-0" />
                            <span className="truncate text-sm md:text-base font-medium text-[#1B3D2A] group-hover:text-[#D4A574] group-hover:underline transition-colors">
                              {item.title}
                            </span>
                          </span>
                          <span className="shrink-0 text-xs md:text-sm text-[#9A8A7A]">{item.date}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
